/**
 * CombatTab — full-sheet combat mode content.
 *
 * Structural parity with d:/dev/d&d/des/charsheet-prototype-v4.html:
 *
 *   Row 1: vitals-unified — 3 sections side-by-side
 *     LEFT  «Состояние»:  saves-mini · HP heart SVG · slider · effects-mini
 *     CENTER «Характеристики»: 6 abilities + sub-block «Боевые навыки» (6 combat skills)
 *     RIGHT «Защита и пассивы»: 3 tiles (КЗ/Иниц/Вним) + «Заметки боя» textarea
 *
 *   Row 2 LEFT  «Что я могу сделать в свой ход»:
 *     economy bar (4 items: Действие / Бонус / Реакция / Движение)
 *     «Оружие» — equipped weapons from inventory (T005)
 *     action-list with icons + 'атака' / 'урон' columns
 *
 *   Row 2 RIGHT «Ресурсы»:
 *     consumables · class-feature pips · spell-slot squares · «Заклинания» list
 *
 * T002: HudCharacterData is structurally assignable to RulesCharacterData
 * (see structural contract in src/lib/character-hud-store.ts and
 * shared/systems/dnd5e/rules/index.ts). useRoll's generic accepts the
 * narrower HUD shape directly — no `as unknown as RulesCharacterData` cast required.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import type { HudCharacterData, CharacterHudHelpers, CharacterHudStore, SkillId, AbilityStat, HudAttack } from '../../lib/character-hud-store';
import { SKILL_TO_STAT, SKILL_LABELS, STAT_LABELS } from '../../lib/character-hud-store';
import { useI18n } from '../../i18n';
import { useRoll } from '../../hooks/useRoll';
import { applyDamageToHp, applyHealingToHp } from '@roll1/shared/systems/dnd5e/damage.js';
import { Stepper, TapEditBlock, TapEditText, EditableRow } from './edit-primitives';
import { InitiativeRollButton } from './character-mode/InitiativeRollButton';
import { DeathBanner, isDeathInputBlocked } from './death-state';
import { useOptionalSessionContext } from '../../contexts/SessionContext';

interface CombatTabProps {
  data: HudCharacterData;
  store: CharacterHudStore;
  helpers: CharacterHudHelpers;
  send?: (msg: any) => void;
  sheetId?: string;
  /** Subscribe to incoming WS messages (V004: cancel roll timeout on roll:result) */
  onMessage?: (handler: (msg: any) => void) => () => void;
}

type ActionType = 'action' | 'bonus' | 'reaction';

const COMBAT_SKILLS: SkillId[] = ['stealth', 'athletics', 'acrobatics', 'perception', 'intimidation', 'medicine'];
const SAVE_STATS: AbilityStat[] = ['wis', 'dex', 'con'];

/* ============================================================
 * HP heart SVG — D-shape pattern path mirroring prototype.
 * ============================================================ */
function HeartSvg({ fillPct, tempPct, isCritical }: { fillPct: number; tempPct: number; isCritical: boolean }) {
  const heartPath =
    'M 50 84 C 30 65, 8 48, 8 28 C 8 14, 20 6, 30 6 C 40 6, 47 14, 50 22 C 53 14, 60 6, 70 6 C 80 6, 92 14, 92 28 C 92 48, 70 65, 50 84 Z';
  return (
    <svg
      viewBox="0 0 100 90"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        color: 'var(--paper-bordo)',
        transition: 'color 0.3s, opacity 0.3s',
      }}
      aria-hidden="true"
    >
      <path d={heartPath} fill="rgba(139, 37, 21, 0.05)" stroke="currentColor" strokeWidth={2.5} strokeLinejoin="round" />
      <path
        d={heartPath}
        fill="currentColor"
        opacity={0.4}
        style={{
          clipPath: `inset(${100 - fillPct}% 0 0 0)`,
          transition: 'clip-path 0.5s cubic-bezier(0.2, 0, 0.2, 1)',
        }}
      />
      {tempPct > 0 && (
        <path
          d={heartPath}
          fill="var(--paper-gold)"
          opacity={0.55}
          style={{
            clipPath: `inset(${100 - tempPct}% 0 0 0)`,
            transition: 'clip-path 0.5s cubic-bezier(0.2, 0, 0.2, 1)',
          }}
        />
      )}
      {isCritical && (
        <style>
          {`@keyframes heart-pulse-${isCritical} { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }`}
        </style>
      )}
    </svg>
  );
}

/* ============================================================
 * AddAttackForm — inline expandable form (T005).
 * Mirrors AddInventoryForm pattern from InventorySection.
 * ============================================================ */
interface AddAttackFormProps {
  onAdd: (attack: HudAttack) => void;
  disabled?: boolean;
}

function AddAttackForm({ onAdd, disabled = false }: AddAttackFormProps) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState('');
  const [bonus, setBonus] = useState(0);
  const [damage, setDamage] = useState('');
  const [range, setRange] = useState('');
  const [damageType, setDamageType] = useState('');

  const reset = (): void => {
    setExpanded(false);
    setName('');
    setBonus(0);
    setDamage('');
    setRange('');
    setDamageType('');
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        disabled={disabled}
        style={{
          marginTop: 8,
          padding: '8px 12px',
          border: '1px dashed var(--paper-rule)',
          borderRadius: 3,
          color: 'var(--color-muted-foreground)',
          fontFamily: 'IBM Plex Sans, sans-serif',
          fontSize: 12,
          background: 'transparent',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        + Добавить атаку
      </button>
    );
  }

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !disabled;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    const attack: HudAttack = {
      id: crypto.randomUUID(),
      name: trimmedName,
      attackBonus: bonus,
      damage: damage.trim(),
      damageType: damageType.trim(),
      ...(range.trim().length > 0 ? { meta: range.trim() } : {}),
    };
    onAdd(attack);
    reset();
  };

  const textInputStyle: React.CSSProperties = {
    padding: '4px 8px',
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: 12,
    background: 'transparent',
    border: '1px solid var(--paper-rule)',
    color: 'var(--color-foreground)',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        marginTop: 8,
        padding: 10,
        border: '1px solid var(--paper-rule)',
        borderRadius: 3,
        backgroundColor: 'var(--paper-bg-2)',
        fontFamily: 'IBM Plex Sans, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <input
        type="text"
        value={name}
        autoFocus
        required
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
        placeholder="Название атаки"
        style={{
          ...textInputStyle,
          fontFamily: 'Fraunces, serif',
          fontSize: 14,
        }}
      />
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--paper-ink-2)' }}>
          <span>Бонус</span>
          <Stepper
            value={bonus}
            onChange={setBonus}
            min={-10}
            max={20}
            disabled={disabled}
            ariaLabel="attack bonus"
          />
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <input
          type="text"
          value={damage}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDamage(e.target.value)}
          placeholder="Урон (1к8+3)"
          style={textInputStyle}
        />
        <input
          type="text"
          value={range}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRange(e.target.value)}
          placeholder="Дистанция"
          style={textInputStyle}
        />
        <input
          type="text"
          value={damageType}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDamageType(e.target.value)}
          placeholder="Тип урона"
          style={textInputStyle}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={reset}
          style={{
            padding: '4px 12px',
            borderRadius: 3,
            background: 'transparent',
            border: '1px solid var(--paper-rule)',
            color: 'var(--color-muted-foreground)',
            fontFamily: 'IBM Plex Sans, sans-serif',
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            padding: '4px 12px',
            borderRadius: 3,
            background: canSubmit ? 'var(--paper-gold)' : 'var(--paper-bg-2)',
            color: canSubmit ? '#1B1714' : 'var(--color-muted-foreground)',
            border: '1px solid var(--paper-rule)',
            fontFamily: 'IBM Plex Sans, sans-serif',
            fontSize: 11,
            fontWeight: 500,
            opacity: canSubmit ? 1 : 0.6,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          Добавить
        </button>
      </div>
    </div>
  );
}

/* ============================================================
 * MAIN COMPONENT
 * ============================================================ */
export function CombatTab({ data, store, helpers, send, sheetId, onMessage }: CombatTabProps) {
  const { t } = useI18n();
  // T004: gate the HUD damage path on the campaign confirmDamage setting, read
  // from the same SessionContext other HUD code consumes. Optional so demo/
  // singleton mode (no provider) simply skips the gate.
  const sessionCtx = useOptionalSessionContext();
  const confirmDamage = sessionCtx?.sessionSettings.confirmDamage ?? false;
  const isReadOnly = !send;
  // Batch B (ADR-010 §8.10): dead character → all mutating controls locked,
  // same visual treatment as read-only mode.
  const isDead = isDeathInputBlocked(data);
  const inputLocked = isReadOnly || isDead;
  const disabledProps = inputLocked ? { 'aria-disabled': true as const, style: { opacity: 0.5, pointerEvents: 'none' as const, cursor: 'not-allowed' as const } } : {};
  // Persisted turn-movement (values.movement_spent) — the server zeroes it on
  // initiative:next alongside action_economy. Was a local useState before the
  // adapter read/write routes existed, so it silently reset on remount/reload.
  const movementSpent = data.combat.movementSpent ?? 0;
  const setMovementSpent = (next: number) => {
    store.patch({ combat: { movementSpent: next } });
  };
  const [editingAttackId, setEditingAttackId] = useState<string | null>(null);

  const hpPercent = data.hp.max > 0 ? (data.hp.current / data.hp.max) * 100 : 100;
  const isLowHp = hpPercent < 35;
  const tempPercent = data.hp.max > 0 ? (data.hp.temp / data.hp.max) * 100 : 0;

  // V001: always call useRoll unconditionally (Rules of Hooks)
  const noopSend = useCallback(() => {}, []);
  // T002: HudCharacterData is structurally assignable to RulesCharacterData —
  // useRoll's generic accepts it directly, no cast required.
  const roll = useRoll(sheetId ?? '', data, send ?? noopSend, onMessage);
  const canRoll = !!(send && sheetId) && !isDead;

  /* ----- Actions ----- */
  const toggleAction = (type: ActionType) => {
    // Persists via store.patch → write-adapter → sheet:update {action_economy};
    // server resets it on initiative:next (ActionEconomyResetSubscriber).
    store.patch({ combat: { actionEconomy: { [type]: !data.combat.actionEconomy[type] } } });
  };
  // BF-001/BF-005 fix: store.patch in adapter mode now dispatches via onPatch
  // (CharacterHudOverlay wires dispatch → toCommands → mp.send). No manual send needed.
  const spendSlot = (level: number) => {
    const slot = data.spells.slots[level];
    if (!slot || slot.current <= 0) return;
    store.patch({ spells: { slots: { [level]: { current: slot.current - 1 } } } });
  };
  const restoreSlot = (level: number) => {
    const slot = data.spells.slots[level];
    if (!slot || slot.current >= slot.max) return;
    store.patch({ spells: { slots: { [level]: { current: slot.current + 1 } } } });
  };
  // Hybrid HP semantics (decision 2026-06-12, see HAD + 644c712 out-of-scope note):
  // the big +/− buttons are the RULES path — damage/healing. store.patch →
  // dispatch → toCommands → sheet:apply-damage/-healing, so temp HP absorbs
  // first and DeathSaveSubscriber sees the damage (dying on 0).
  const setHpDelta = useCallback((delta: number) => {
    if (delta < 0) {
      // T004: when the GM enabled "confirm damage", gate the HUD damage path with
      // the same window.confirm prompt TokenManager.handleHpConfirm uses. Healing
      // (delta >= 0) is never gated. Cancel → apply nothing.
      if (confirmDamage) {
        const msg = t('combat.confirmDamagePrompt')
          .replace('{damage}', String(-delta))
          .replace('{name}', data.identity.name);
        if (!window.confirm(msg)) return;
      }
      // Damage: absorb into temp HP first via the SHARED model — identical to the
      // server, so the optimistic overlay matches the echo and the reconcile
      // never sticks (fixes the temp-HP "wrong predicted HP for 5s + false
      // timeout toast" regression). toCommands folds the temp drop into the
      // apply-damage amount, so the command still carries the raw damage.
      const { hp, temp } = applyDamageToHp(data.hp.current, data.hp.temp, -delta);
      store.patch({ hp: { current: hp, temp } });
    } else if (delta > 0) {
      store.patch({ hp: { current: applyHealingToHp(data.hp.current, data.hp.max, delta) } });
    }
  }, [data.hp.max, data.hp.current, data.hp.temp, data.identity.name, confirmDamage, t, store]);
  // …while the numeric Stepper is the EDITOR path — a direct set of values.hp
  // (GM correction). It bypasses damage semantics on purpose: no temp-HP
  // absorption (which made "set HP to X" look dead while temp drained) and no
  // death-save auto-trigger. Demo/singleton mode falls back to store.patch.
  const setHpDirect = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(data.hp.max, next));
    if (send && sheetId) {
      send({ type: 'sheet:update', sheetId, changes: { hp: clamped } });
    } else {
      store.patch({ hp: { current: clamped } });
    }
  }, [data.hp.max, send, sheetId, store]);
  const mockRoll = (name: string, bonus: number) => {
    const r = Math.floor(Math.random() * 20) + 1;
    const total = r + bonus;
    toast(t('characterHud.mockRoll'), { description: `${name}: 1d20${helpers.fmt(bonus)} = ${total} (${r})` });
  };

  /* ----- T005: attack mutation helpers — all route through store.patch on combat.attacks.
     Write-adapter rule 8 ('attacks' sectionKey under repeating-row) converts diffs to
     sheet:repeating-row-add / -update / -remove. ----- */
  const handleAddAttack = (attack: HudAttack): void => {
    store.patch({ combat: { attacks: [...data.combat.attacks, attack] } });
  };
  const handleDeleteAttack = (id: string): void => {
    store.patch({ combat: { attacks: data.combat.attacks.filter((a) => a.id !== id) } });
    if (editingAttackId === id) setEditingAttackId(null);
  };
  const handleEditAttack = (id: string, patch: Partial<HudAttack>): void => {
    store.patch({
      combat: {
        attacks: data.combat.attacks.map((a) => (a.id === id ? { ...a, ...patch } : a)),
      },
    });
  };

  /* ----- Derived combat data ----- */
  const slotLevels = Object.keys(data.spells.slots).map(Number).sort((a, b) => a - b);
  const consumables = data.resources.filter((r) => r.max > 0);
  const featureUses = data.features.filter((f) => f.uses && f.uses.max > 0);
  const noResources = consumables.length === 0 && featureUses.length === 0 && slotLevels.length === 0;
  // T005: equipped weapons row uses the `weapon` marker on HudItem to keep
  // armor and ammo out (chain-shirt/arrows are equipped but not weapons).
  const equippedWeapons = data.inventory.filter((i) => i.equipped === true && i.weapon === true);
  const preparedSpells = data.spells.known.filter((s) => s.state === 'prepared' || s.state === 'always');
  // Potion quick-slots removed 2026-07-03 (batch A): they were hardcoded
  // placeholders with no onClick — dead UI. A real quick-use feature (find
  // potions in inventory → decrement → healing roll) lives in the backlog:
  // docs/backlog/hud-backlog-plan-2026-07-03.md, батч F.

  /* ----- Action icon resolver ----- */
  const actionIcon = (idx: number, meta: string | undefined): string => {
    if (meta?.toLowerCase().includes('лук') || meta?.toLowerCase().includes('дальн')) return '⌖';
    if (meta?.toLowerCase().includes('клинк') || meta?.toLowerCase().includes('2 удара')) return '✕';
    if (meta?.toLowerCase().includes('способ') || meta?.toLowerCase().includes('лечен')) return '❖';
    return ['⌖', '✕', '❖', '+', '⌒', '↦'][idx % 6];
  };

  return (
    <div className="flex flex-col gap-6">
      {/* ============================================================
            ROW 1 — VITALS UNIFIED (3 columns)
            Section labels live OUTSIDE the bordered card (proto pattern).
          ============================================================ */}
      <div className="flex flex-col" style={{ gap: 6 }}>
        {/* Section-labels header — 3 cols matching the card below */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 1fr) minmax(260px, 1fr) minmax(260px, 1fr)',
            gap: 0,
            paddingInline: 2,
          }}
        >
          <div className="paper-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>Состояние</span>
            {isLowHp && <span style={{ color: 'var(--paper-bordo)', fontWeight: 600 }}>Опасно</span>}
          </div>
          <div className="paper-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingLeft: 12 }}>
            <span>Характеристики</span>
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'var(--paper-ink-2)', fontSize: 9 }}>
              тапни — чистый бросок
            </span>
          </div>
          <div className="paper-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingLeft: 12 }}>
            <span>Защита и пассивы</span>
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'var(--paper-ink-2)', fontSize: 9 }}>
              тапни — разбор
            </span>
          </div>
        </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 1fr) minmax(260px, 1fr) minmax(260px, 1fr)',
          gap: 0,
          border: '1px solid var(--paper-rule)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* ───── LEFT: «Состояние» (saves + heart + effects) ───── */}
        <div
          style={{
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            borderRight: '1px solid var(--paper-rule)',
            background: isLowHp ? 'linear-gradient(180deg, var(--paper-bg) 0%, rgba(200,51,30,0.06) 100%)' : 'transparent',
          }}
        >
          <DeathBanner data={data} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '72px auto 1fr',
              columnGap: 8,
              rowGap: 4,
            }}
          >
            {/* Saves-mini (3 ability saves) — vertical column, 1st column */}
            <div
              style={{
                gridColumn: 1,
                gridRow: '1 / span 2',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid var(--paper-rule)',
                background: 'var(--paper-bg)',
              }}
            >
              {SAVE_STATS.map((stat, i) => {
                const isLast = i === SAVE_STATS.length - 1;
                const isProf = data.saves[stat] === 1;
                const mod = helpers.activeSaveMod(data, stat);
                return (
                  <button
                    key={stat}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px 4px',
                      borderBottom: isLast ? 'none' : '1px solid var(--paper-rule)',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => canRoll ? roll.rollSave(stat) : mockRoll(`${STAT_LABELS[stat]} save`, mod)}
                  >
                    <span className="paper-label" style={{ fontSize: 10 }}>
                      {isProf && <span style={{ color: 'var(--paper-bordo)', marginRight: 3, fontSize: 7 }}>●</span>}
                      {STAT_LABELS[stat]}
                    </span>
                    <span className="paper-num" style={{ fontSize: 26, fontStyle: 'normal', fontWeight: 600 }}>
                      {helpers.fmt(mod)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Heart + slider (2nd column, row 2) */}
            <div
              style={{
                gridColumn: 2,
                gridRow: 2,
                display: 'flex',
                gap: 4,
                alignItems: 'stretch',
                height: 116,
              }}
            >
              <div
                tabIndex={0}
                className="hp-heart-container"
                style={{
                  position: 'relative',
                  width: 100,
                  height: 116,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={`${data.hp.current} / ${data.hp.max}${data.hp.temp ? ` (+${data.hp.temp})` : ''}`}
              >
                <HeartSvg
                  fillPct={hpPercent}
                  tempPct={tempPercent}
                  isCritical={isLowHp}
                />
                {/* Numbers overlay — proto fades them in only on hover/critical/focus.
                    We use CSS class on parent + scoped style block below. */}
                <div
                  className="hp-numbers-overlay"
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 4,
                    pointerEvents: 'none',
                    textShadow: '0 0 8px rgba(244,239,227,0.9), 0 0 4px rgba(244,239,227,0.6)',
                    opacity: isLowHp ? 1 : 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <span style={{ fontFamily: 'Fraunces, serif', fontSize: 32, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--paper-ink)' }}>
                    {data.hp.current}
                  </span>
                  {data.hp.temp > 0 ? (
                    <span style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--paper-gold)', fontWeight: 600, marginLeft: 2 }}>
                      +{data.hp.temp}
                    </span>
                  ) : (
                    <>
                      <span style={{ fontFamily: 'Fraunces, serif', fontSize: 18, color: 'var(--paper-ink-2)', fontWeight: 300 }}>/</span>
                      <span style={{ fontFamily: 'Fraunces, serif', fontSize: 18, color: 'var(--paper-ink-2)', fontWeight: 400 }}>
                        {data.hp.max}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBlock: 4,
                  gap: 4,
                }}
              >
                <button
                  onClick={() => setHpDelta(+1)}
                  style={{ width: 22, height: 22, border: '1px solid var(--paper-rule)', borderRadius: 11, background: 'var(--paper-bg)', cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--paper-ink-2)' }}
                  title="+1 HP"
                  {...disabledProps}
                >
                  +
                </button>
                <Stepper
                  value={data.hp.current}
                  onChange={setHpDirect}
                  min={0}
                  max={data.hp.max}
                  disabled={!send}
                  ariaLabel="Hit points"
                  label="HP"
                  suffix={` / ${data.hp.max}`}
                />
                <button
                  onClick={() => setHpDelta(-1)}
                  style={{ width: 22, height: 22, border: '1px solid var(--paper-rule)', borderRadius: 11, background: 'var(--paper-bg)', cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--paper-ink-2)' }}
                  title="-1 HP"
                  {...disabledProps}
                >
                  −
                </button>
                <div style={{display:'flex', flexDirection:'column', gap:2, fontSize: 11, opacity: 0.85}}>
                  <Stepper value={data.hp.max} onChange={(next: number) => store.patch({ hp: { max: next } })} min={1} max={999} disabled={!send} ariaLabel="Максимум HP" label="Макс" />
                  <Stepper value={data.hp.temp} onChange={(next: number) => store.patch({ hp: { temp: next } })} min={0} max={99} disabled={!send} ariaLabel="Временные HP" label="Врем" />
                </div>
              </div>
            </div>

            {/* Effects-mini (right column) — compact list, proto-style ✦ items */}
            <div
              style={{
                gridColumn: 3,
                gridRow: '1 / span 2',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                overflowY: 'auto',
                maxHeight: 152,
                paddingLeft: 4,
              }}
            >
              {data.activeEffects.length === 0 && (
                <div
                  className="paper-label"
                  style={{
                    padding: '6px 2px',
                    color: 'var(--paper-ink-2)',
                    opacity: 0.6,
                    fontSize: 9,
                  }}
                >
                  эффектов нет
                </div>
              )}
              {data.activeEffects.map((eff) => (
                <button
                  key={eff.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '10px 1fr',
                    alignItems: 'baseline',
                    gap: 5,
                    padding: '2px 0',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  title={eff.description ?? eff.name}
                >
                  <span style={{ fontFamily: 'Fraunces, serif', fontSize: 12, fontStyle: 'italic', color: 'var(--paper-gold)', lineHeight: 1 }}>
                    ✦
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.15 }}>
                    <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--paper-ink)' }}>
                      {eff.name}
                    </span>
                    {eff.duration && (
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: 'var(--paper-ink-2)', letterSpacing: '0.04em' }}>
                        {eff.duration}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ───── CENTER: «Характеристики» ───── */}
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12, borderRight: '1px solid var(--paper-rule)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {(['str', 'dex', 'con', 'int', 'wis', 'cha'] as AbilityStat[]).map((stat) => {
              const val = data.stats[stat];
              const mod = helpers.mod(val);
              return (
                <button
                  key={stat}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid var(--paper-rule)',
                    borderRadius: 3,
                    background: 'var(--paper-bg)',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => canRoll ? roll.rollAbilityCheck(stat) : mockRoll(STAT_LABELS[stat], mod)}
                >
                  <div className="paper-label" style={{ fontSize: 9 }}>{STAT_LABELS[stat]}</div>
                  <div className="paper-num" style={{ fontSize: 22 }}>{helpers.fmt(mod)}</div>
                  <div className="paper-mono" style={{ fontSize: 10, color: 'var(--paper-ink-2)' }}>{val}</div>
                </button>
              );
            })}
          </div>

          {/* Sub-block: Боевые навыки */}
          <div className="paper-label" style={{ paddingBottom: 6, borderBottom: '1px solid var(--paper-rule)' }}>
            Боевые навыки
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
            {COMBAT_SKILLS.map((skill) => {
              const mod = helpers.activeSkillMod(data, skill);
              const isProf = data.skills[skill] > 0;
              return (
                <button
                  key={skill}
                  style={{
                    padding: 6,
                    border: '1px solid var(--paper-rule)',
                    borderRadius: 3,
                    background: 'var(--paper-bg)',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => canRoll ? roll.rollSkill(skill) : mockRoll(SKILL_LABELS[skill], mod)}
                >
                  <div className="paper-label" style={{ fontSize: 8 }}>
                    {SKILL_LABELS[skill]}
                  </div>
                  <div className="paper-num" style={{ fontSize: 16 }}>{helpers.fmt(mod)}</div>
                  <div className="paper-mono" style={{ fontSize: 9, color: 'var(--paper-ink-2)' }}>
                    {isProf && <span style={{ color: 'var(--paper-bordo)', fontSize: 7 }}>●</span>}{STAT_LABELS[SKILL_TO_STAT[skill]]}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ───── RIGHT: «Защита и пассивы» ───── */}
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
            <div style={{ padding: 8, border: '1px solid var(--paper-rule)', borderRadius: 3, background: 'var(--paper-bg)', textAlign: 'center' }}>
              <div className="paper-num" style={{ fontSize: 22 }}>{data.ac}</div>
              <div className="paper-label">КЗ</div>
            </div>
            <div style={{ padding: 8, border: '1px solid var(--paper-rule)', borderRadius: 3, background: 'var(--paper-bg)', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              {/* Wave 6: display computed total (read-only); user edits the bonus.
                  Template formula `initiative = dex_mod + initiative_bonus`
                  overwrites the total на каждом recalc, so we surface the
                  editable bonus piece instead of binding Stepper к total. */}
              <div className="paper-num" style={{ fontSize: 22 }}>
                {data.combat.initiative >= 0 ? `+${data.combat.initiative}` : data.combat.initiative}
              </div>
              <Stepper
                value={data.combat.initiativeBonus ?? 0}
                onChange={(next: number) => store.patch({ combat: { initiativeBonus: next } })}
                min={-20}
                max={20}
                disabled={!send}
                label="Бонус"
                ariaLabel="Бонус к инициативе"
              />
              <InitiativeRollButton data={data} store={store} sheetId={sheetId} send={send} onMessage={onMessage} />
              <div className="paper-label">Иниц.</div>
            </div>
            <div style={{ padding: 8, border: '1px solid var(--paper-rule)', borderRadius: 3, background: 'var(--paper-bg)', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              {/* Wave 6: passive perception = 10 + wis_mod + passive_perception_bonus.
                  Display includes bonus; Stepper edits the bonus piece. */}
              <div className="paper-num" style={{ fontSize: 22 }}>
                {helpers.passive(helpers.activeSkillMod(data, 'perception')) + (data.passivePerceptionBonus ?? 0)}
              </div>
              <Stepper
                value={data.passivePerceptionBonus ?? 0}
                onChange={(next: number) => store.patch({ passivePerceptionBonus: next })}
                min={-20}
                max={20}
                disabled={!send}
                label="Бонус"
                ariaLabel="Бонус к пасс. восприятию"
              />
              <div className="paper-label">Вним.</div>
            </div>
          </div>

          {/* Notes */}
          <div className="paper-label" style={{ paddingBottom: 6, borderBottom: '1px solid var(--paper-rule)' }}>
            Заметки боя
          </div>
          <TapEditBlock
            value={data.combat.notes ?? ''}
            onChange={(next: string) => store.patch({ combat: { notes: next } })}
            disabled={!send}
            placeholder="Заметки боя..."
            rows={3}
          />
        </div>
      </div>
      </div>

      {/* ============================================================
            ROW 2 — actions (LEFT) + resources (RIGHT)
            Labels live OUTSIDE each card.
          ============================================================ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12, minWidth: 0 }}>
        {/* ───── LEFT: «Что я могу сделать в свой ход» ───── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <div className="paper-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>Что я могу сделать в свой ход</span>
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'var(--paper-ink-2)', fontSize: 9 }}>
              тап — бросок · ⓘ — описание
            </span>
          </div>

          {/* Economy bar with 4 items */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
            {(['action', 'bonus', 'reaction'] as ActionType[]).map((type) => {
              const spent = !data.combat.actionEconomy[type];
              const labelKey = type === 'action'
                ? 'characterHud.actionAction'
                : type === 'bonus' ? 'characterHud.actionBonus'
                : 'characterHud.actionReaction';
              return (
                <button
                  key={type}
                  className={`paper-action-tile ${spent ? 'spent' : ''}`}
                  style={{ padding: '6px 10px', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={() => toggleAction(type)}
                  {...disabledProps}
                >
                  <span className="paper-action-dot" />
                  <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--paper-ink)' }}>
                    {t(labelKey)}
                  </span>
                </button>
              );
            })}
            <button
              className={`paper-action-tile ${movementSpent >= data.speed ? 'spent' : ''}`}
              style={{ padding: '6px 10px', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
              onClick={() => setMovementSpent(movementSpent >= data.speed ? 0 : Math.min(movementSpent + 5, data.speed))}
              {...disabledProps}
            >
              <span className="paper-action-dot" />
              {/* Overspend (Dash / map auto-spend) reads as a NEGATIVE remainder —
                  clamping to 0 hid that the budget was blown. */}
              <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 11, fontWeight: 500, color: data.speed - movementSpent < 0 ? 'var(--paper-bordo)' : 'var(--paper-ink)' }}>
                Движение {data.speed - movementSpent}/{data.speed}
              </span>
            </button>
          </div>

          {/* T005: equipped weapons strip — display-only, paper-action-tile styling,
              respects disabledProps so it's read-only when !send. */}
          {equippedWeapons.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div className="paper-label" style={{ fontSize: 9 }}>Оружие</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} {...disabledProps}>
                {equippedWeapons.map((w) => (
                  <div
                    key={w.id}
                    className="paper-action-tile"
                    style={{
                      padding: '4px 10px',
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'default',
                    }}
                    title={w.description ?? w.name}
                  >
                    <span className="paper-action-dot" />
                    <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--paper-ink)' }}>
                      {w.name}
                      {w.quantity > 1 && (
                        <span style={{ color: 'var(--paper-ink-2)', marginLeft: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
                          ×{w.quantity}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action list with icons + 'атака' / 'урон' columns — T005: each row is now
              an EditableRow. View = existing tap-to-roll button. Edit = inline form
              (name/bonus/damage/range/damageType). Delete via × button. */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {data.combat.attacks.map((atk, idx) => (
              <EditableRow
                key={atk.id}
                isEditing={editingAttackId === atk.id}
                onEditStart={() => setEditingAttackId(atk.id)}
                onEditEnd={() => setEditingAttackId(null)}
                onDelete={() => handleDeleteAttack(atk.id)}
                renderView={() => (
                  <button
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '36px 1fr auto auto',
                      gap: 12,
                      alignItems: 'center',
                      padding: '8px 6px',
                      borderBottom: '1px solid var(--paper-rule)',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      width: '100%',
                    }}
                    onClick={() => canRoll ? roll.rollAttack(atk) : mockRoll(atk.name, atk.attackBonus)}
                  >
                    <span
                      style={{
                        width: 32, height: 32,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid var(--paper-gold)', borderRadius: 3,
                        background: 'var(--paper-bg)',
                        fontFamily: 'Fraunces, serif',
                        fontStyle: 'italic',
                        fontSize: 20,
                        color: 'var(--paper-bordo)',
                        fontWeight: 600,
                      }}
                    >
                      {actionIcon(idx, atk.meta)}
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 13, fontWeight: 500, color: 'var(--paper-ink)' }}>
                        {atk.name}
                      </span>
                      {atk.meta && (
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--paper-ink-2)' }}>
                          {atk.meta}
                        </span>
                      )}
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 56 }}>
                      <div className="paper-mono" style={{ fontSize: 14, color: 'var(--paper-ink)' }}>{helpers.fmt(atk.attackBonus)}</div>
                      <div className="paper-label" style={{ fontSize: 8 }}>атака</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 80, paddingRight: 60 }}>
                      <div className="paper-mono" style={{ fontSize: 13, color: 'var(--paper-bordo)' }}>{atk.damage} {atk.damageType}</div>
                      <div className="paper-label" style={{ fontSize: 8 }}>урон</div>
                    </div>
                  </button>
                )}
                renderEdit={() => (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.4fr 90px 1fr 1fr 1fr',
                      gap: 6,
                      padding: '8px 130px 8px 6px',
                      borderBottom: '1px solid var(--paper-rule)',
                      alignItems: 'center',
                    }}
                  >
                    <TapEditText
                      value={atk.name}
                      onChange={(v: string) => handleEditAttack(atk.id, { name: v })}
                      disabled={!send}
                      placeholder="Название"
                    />
                    <Stepper
                      value={atk.attackBonus}
                      onChange={(v: number) => handleEditAttack(atk.id, { attackBonus: v })}
                      min={-10}
                      max={20}
                      disabled={!send}
                      ariaLabel="attack bonus"
                    />
                    <TapEditText
                      value={atk.damage}
                      onChange={(v: string) => handleEditAttack(atk.id, { damage: v })}
                      disabled={!send}
                      placeholder="Урон"
                    />
                    <TapEditText
                      value={atk.meta ?? ''}
                      onChange={(v: string) => handleEditAttack(atk.id, { meta: v.length > 0 ? v : undefined })}
                      disabled={!send}
                      placeholder="Дистанция"
                    />
                    <TapEditText
                      value={atk.damageType}
                      onChange={(v: string) => handleEditAttack(atk.id, { damageType: v })}
                      disabled={!send}
                      placeholder="Тип урона"
                    />
                  </div>
                )}
              />
            ))}
            <AddAttackForm onAdd={handleAddAttack} disabled={!send} />
          </div>
        </div>

        {/* ───── RIGHT: «Ресурсы» ───── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <div className="paper-section-label">Ресурсы</div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: 10,
              border: '1px solid var(--paper-rule)',
              borderRadius: 3,
              background: 'var(--paper-bg)',
            }}
          >
            {noResources && (
              <div className="paper-label" style={{ padding: '6px 2px', color: 'var(--paper-ink-2)', opacity: 0.6, fontSize: 9 }}>
                ресурсов нет
              </div>
            )}
            {/* Consumables */}
            {consumables.map((res) => (
              <div key={res.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0' }}>
                <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 12, color: 'var(--paper-ink)' }}>
                  {res.name}
                </span>
                <span className="paper-mono" style={{ fontSize: 12 }}>
                  {res.current}<small style={{ color: 'var(--paper-ink-2)', fontWeight: 400 }}>/{res.max}</small>
                </span>
              </div>
            ))}
            {consumables.length > 0 && featureUses.length > 0 && (
              <div style={{ borderTop: '1px dashed var(--paper-rule)', margin: '4px 0' }} />
            )}

            {/* Class feature pips — square (matches proto .resource-pip.feature) */}
            {featureUses.map((feat) => {
              const max = feat.uses!.max;
              const cur = feat.uses!.current;
              return (
                <div key={feat.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0' }}>
                  <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 12, color: 'var(--paper-ink)' }}>
                    <span style={{ color: 'var(--paper-bordo)', marginRight: 6, fontFamily: 'Fraunces, serif', fontSize: 14 }}>❖</span>
                    {feat.name}
                  </span>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {Array.from({ length: max }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`paper-pip-square ${i < cur ? '' : 'spent'}`}
                        aria-label={`${feat.name} ${i + 1}`}
                        {...disabledProps}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Spell slot squares */}
            {slotLevels.length > 0 && (
              <>
                {(consumables.length > 0 || featureUses.length > 0) && (
                  <div style={{ borderTop: '1px dashed var(--paper-rule)', margin: '4px 0' }} />
                )}
                {slotLevels.map((lvl) => {
                  const slot = data.spells.slots[lvl];
                  return (
                    <div key={lvl} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0' }}>
                      <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 12, color: 'var(--paper-ink)' }}>
                        <span style={{ color: 'var(--paper-gold)', marginRight: 6, fontFamily: 'Fraunces, serif', fontSize: 14, fontStyle: 'italic' }}>✦</span>
                        Слоты {lvl} круга
                      </span>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {Array.from({ length: slot.max }).map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            className={`paper-pip-slot ${i < slot.current ? '' : 'spent'}`}
                            onClick={() => (i < slot.current ? spendSlot(lvl) : restoreSlot(lvl))}
                            aria-label={`slot ${lvl} ${i + 1}`}
                            {...disabledProps}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Spells list — ✦ icon + name + meta + level pill.
              T005: gate on the filtered (prepared/always) list so the header
              doesn't render when nothing would be listed. Filter predicate
              itself is unchanged per task contract. */}
          {preparedSpells.length > 0 && (
            <>
              <div className="paper-section-label" style={{ marginTop: 6 }}>Заклинания</div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid var(--paper-rule)',
                  borderRadius: 3,
                  background: 'var(--paper-bg)',
                  overflow: 'hidden',
                }}
              >
                {data.spells.known
                  .filter((s) => s.state === 'prepared' || s.state === 'always')
                  .map((spell) => (
                    <button
                      key={spell.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '20px 1fr auto',
                        gap: 8,
                        alignItems: 'center',
                        padding: '6px 10px',
                        borderBottom: '1px solid var(--paper-rule)',
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ color: 'var(--paper-gold)', fontFamily: 'Fraunces, serif', fontStyle: 'italic', fontSize: 14 }}>✦</span>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 12, fontWeight: 500, color: 'var(--paper-ink)' }}>
                          {spell.name}
                          {spell.concentration && <span style={{ color: 'var(--paper-gold)', marginLeft: 6, fontSize: 10 }}>конц.</span>}
                        </span>
                        {spell.castingTime && (
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--paper-ink-2)' }}>
                            {spell.castingTime}{spell.range && ` · ${spell.range}`}
                          </span>
                        )}
                      </div>
                      <span className="paper-mono" style={{ fontSize: 10, color: 'var(--paper-ink-2)' }}>
                        {spell.level === 0 ? 'заг.' : `${spell.level} кр.`}
                      </span>
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CombatTab;
